import { forwardRef, useEffect, useRef, useState, type ReactNode } from 'react';
import { cn } from '../../lib/utils';
import { Badge } from '../data-display/Badge';

export interface Tab {
  id: string;
  label: string;
  content?: ReactNode;
  count?: number;
}

export interface TabsProps {
  tabs: Tab[];
  defaultTab?: string;
  activeTab?: string;
  onTabChange?: (tabId: string) => void;
  className?: string;
  /**
   * @deprecated Sans effet : la barre defile desormais TOUJOURS
   * horizontalement quand les onglets ne tiennent pas (ecran etroit, panneau
   * lateral), au lieu de les comprimer. Quand ils tiennent, rien ne change.
   * Garde pour ne pas casser les appels existants.
   */
  scrollable?: boolean;
}

/**
 * Ramene l'onglet actif dans la partie visible de la barre, en ne faisant
 * defiler QUE la barre (jamais la page, contrairement a `scrollIntoView`).
 * Ne fait rien quand tous les onglets tiennent.
 */
function revealActiveTab(list: HTMLElement | null) {
  if (!list || list.scrollWidth <= list.clientWidth) return;
  const active = list.querySelector<HTMLElement>('[aria-selected="true"]');
  if (!active) return;
  const listRect = list.getBoundingClientRect();
  const tabRect = active.getBoundingClientRect();
  if (tabRect.left < listRect.left) {
    list.scrollLeft -= listRect.left - tabRect.left;
  } else if (tabRect.right > listRect.right) {
    list.scrollLeft += tabRect.right - listRect.right;
  }
}

const Tabs = forwardRef<HTMLDivElement, TabsProps>(
  // `scrollable` n'est plus lu (deprecie, cf. TabsProps).
  ({ tabs, defaultTab, activeTab: controlledActiveTab, onTabChange, className }, ref) => {
    const [internalActiveTab, setInternalActiveTab] = useState(
      defaultTab || tabs[0]?.id || ''
    );

    const isControlled = controlledActiveTab !== undefined;
    const activeTab = isControlled ? controlledActiveTab : internalActiveTab;

    const handleTabClick = (tabId: string) => {
      if (!isControlled) {
        setInternalActiveTab(tabId);
      }
      onTabChange?.(tabId);
    };

    const activeTabContent = tabs.find((tab) => tab.id === activeTab)?.content;

    const listRef = useRef<HTMLDivElement>(null);
    useEffect(() => {
      revealActiveTab(listRef.current);
    }, [activeTab]);

    return (
      <div ref={ref} className={className}>
        {/* Tab List */}
        <div
          ref={listRef}
          className={cn(
            'flex border-b border-neutral-200',
            // Defilement horizontal quand les onglets ne tiennent pas, sans
            // barre visible : la barre native mangerait la hauteur de
            // l'onglet ; on defile a la molette, au pave ou au doigt.
            'overflow-x-auto [scrollbar-width:none] [&::-webkit-scrollbar]:hidden'
          )}
          role="tablist"
          aria-label="Onglets"
        >
          {tabs.map((tab) => (
            <button
              key={tab.id}
              onClick={() => handleTabClick(tab.id)}
              role="tab"
              aria-selected={activeTab === tab.id}
              aria-controls={`tabpanel-${tab.id}`}
              id={`tab-${tab.id}`}
              className={cn(
                'relative flex shrink-0 items-center gap-2 whitespace-nowrap px-4 py-3 text-sm font-medium transition-colors',
                'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-primary-500',
                activeTab === tab.id
                  ? 'text-primary-600'
                  : 'text-neutral-500 hover:text-neutral-700'
              )}
            >
              {tab.label}
              {tab.count !== undefined && (
                <Badge variant={activeTab === tab.id ? 'primary' : 'neutral'}>
                  {tab.count}
                </Badge>
              )}
              {activeTab === tab.id && (
                <span className="absolute bottom-0 left-0 right-0 h-0.5 bg-primary-500" />
              )}
            </button>
          ))}
        </div>

        {/* Tab Content */}
        {activeTabContent && (
          <div
            role="tabpanel"
            id={`tabpanel-${activeTab}`}
            aria-labelledby={`tab-${activeTab}`}
            className="pt-4"
          >
            {activeTabContent}
          </div>
        )}
      </div>
    );
  }
);

Tabs.displayName = 'Tabs';

export { Tabs };
